import { lazy, useCallback, useEffect, useRef } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTheme, useMediaQuery, DialogProps } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { useAnalytics } from '@/analytics/useAnalytics';
import {
  WorkflowDtoStatus,
  WorkflowCreateRequest,
  getListWorkflowsQueryKey,
  WorkflowMeetingsContextDtoTimeRange
} from '@/api/generated';
import {
  FormValues,
  initialValues,
  formSchema,
  LocalCapabilityType,
  DEFAULT_TIME_RANGE
} from '@/components/AiWorkflow/formSchema';
import { useWorkflow } from '@/components/AiWorkflow/hooks/useWorkflow';
import { ShowFnOutput } from '@/hooks/useModal/ModalContextTypes';
import useModal from '@/hooks/useModal/useModal';
import { useSelectedGoogleDriveFiles, useGoogleDriveActions } from '@/store/googleDriveStore';
import { useRoutingActions } from '@/store/routingStore';
import { launchSuccessToast, launchErrorToast } from '@/utils/toast';

interface UseAiWorkflowLogicProps {
  onClose: () => void;
  editWorkflow: LocalCapabilityType | null;
  setEditWorkflow: (workflow: LocalCapabilityType | null) => void;
}

const LazyActionModal = lazy(() => import('@/components/modals/ActionModal/ActionModal'));

export const useAiWorkflowLogic = ({
  onClose,
  editWorkflow,
  setEditWorkflow
}: UseAiWorkflowLogicProps) => {
  const { setRoutingFunction, setIsRouteFunction } = useRoutingActions();
  const { showModal } = useModal();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const { captureEvent } = useAnalytics();
  const { createWorkflow, patchWorkflow, isCreateLoading, isPatchLoading } = useWorkflow();
  const queryClient = useQueryClient();
  const listWorkflowsQueryKey = getListWorkflowsQueryKey();
  const modalRef = useRef<ShowFnOutput<DialogProps> | null>(null);
  const selectedGoogleDriveFiles = useSelectedGoogleDriveFiles();
  const { clearSelectedGoogleDriveFiles } = useGoogleDriveActions();
  const isEdit = !!editWorkflow;
  const submitText = editWorkflow?.createdAt ? 'Save Changes' : 'Create Agent';
  const defaultValues = isEdit
    ? {
        ...editWorkflow,
        status: editWorkflow.status ?? WorkflowDtoStatus.disabled,
        notifications: { email: editWorkflow.notifications?.email ?? false },
        createdFrom: editWorkflow.createdFrom ?? undefined
      }
    : initialValues;

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
    mode: 'onTouched'
  });

  const { handleSubmit, setValue, watch, formState } = methods;
  const isUnsaved = formState.isDirty && Object.keys(formState.touchedFields).length > 0;
  const hasErrors =
    Object.keys(formState.errors).length > 0 || watch('timeRange') === DEFAULT_TIME_RANGE;

  useEffect(() => {
    if (isUnsaved) {
      setIsRouteFunction(true);
      setRoutingFunction(showProgressModal);
    } else {
      setIsRouteFunction(false);
      setRoutingFunction(() => null);
    }
    return () => {
      setIsRouteFunction(false);
      setRoutingFunction(() => null);
    };
  }, [isUnsaved, hasErrors]);

  const handleLeave = useCallback(() => {
    if (isUnsaved) {
      showProgressModal();
    } else {
      onClose();
    }
  }, [isUnsaved, onClose, hasErrors]);

  const showProgressModal = (routeFunction?: () => void) => {
    modalRef.current = showModal(LazyActionModal, {
      onClose: () => {
        if (routeFunction) {
          routeFunction();
        } else {
          onClose();
        }
        modalRef.current?.hide();
      },
      onSubmit: () => {
        if (hasErrors) {
          modalRef.current?.hide();
          return;
        }
        handleSubmit(onSubmit)();
      },
      title: 'Save your progress?',
      description: 'You have made some changes. Do you want to save them before exiting?',
      actionTitle: 'Save and Exit',
      dismissTitle: 'Discard'
    });
    modalRef.current.destroy = () => {
      routeFunction?.();
      modalRef.current?.hide();
    };
  };

  const createRequestData = (values: FormValues): WorkflowCreateRequest => ({
    title: values.title,
    flow: {
      prompt: values.prompt
    },
    context: {
      meetings: {
        timeRange: values.timeRange as WorkflowMeetingsContextDtoTimeRange,
        scope: values.scope,
        keywords: values.keyWords
      },
      googleDrive: {
        // this is not in the form schema, but we need to send it to the backend
        files: selectedGoogleDriveFiles.map(file => ({
          fileId: file.fileId ?? '',
          fileName: file.fileName ?? ''
        }))
      }
    },
    notifications: {
      email: values.notifications.email ? { type: 'email' } : undefined
    },
    trigger: {
      scheduled: {
        daysOfWeek: values.trigger,
        time: new Date().toISOString().substring(11, 16)
      }
    },
    status: values.status,
    createdFrom: values.createdFrom
  });

  const onSubmit: SubmitHandler<FormValues> = values => {
    const requestData = createRequestData(values);
    if (isEdit && editWorkflow?.id) {
      patchWorkflow(
        { id: editWorkflow.id, data: requestData },
        {
          onSuccess: () => {
            captureEvent('agent_capability.updated', { requestData });
            launchSuccessToast('Agent updated');
            queryClient.invalidateQueries({ queryKey: listWorkflowsQueryKey });
            setEditWorkflow(null);
            onClose();
          },
          onError: () => {
            launchErrorToast('Failed to update agent');
          },
          onSettled: () => {
            clearSelectedGoogleDriveFiles();
            modalRef.current?.destroy();
          }
        }
      );
    } else {
      createWorkflow(
        { data: requestData },
        {
          onSuccess: () => {
            captureEvent('agent_capability.created', { createRequestData });
            launchSuccessToast('Agent created');
            queryClient.invalidateQueries({ queryKey: listWorkflowsQueryKey });
            onClose();
          },
          onError: () => {
            launchErrorToast('Failed to create agent');
          },
          onSettled: () => {
            modalRef.current?.destroy();
          }
        }
      );
    }
  };

  return {
    // Form methods
    methods,
    handleSubmit,
    setValue,
    watch,
    formState,

    // State
    isEdit,
    submitText,
    isLargeScreen,
    isUnsaved,

    // Loading states
    isCreateLoading,
    isPatchLoading,

    // Handlers
    handleLeave,
    onSubmit
  };
};
